// The boarding look (mockup round 111 card 01 Grapple Lines,
// v2-the-deep.md §8.6, ui-map-interface.md §6.1.13, R2721).
//
// A pirate hull boards a support hull: two grapple lines run from the
// pirate to the hull, and a thin ring round the hull fills with the share
// of the boarding done. When the hull is taken, the swallowtail wipes over
// the class mark from the bow to the stern. When a combat hull of the side
// stops the boarding, the lines snap back to their middle and the ring
// drains.
//
// The fleet layer owns the state: the boarding columns of each hull, the
// snaps and the hand-overs. This module holds the figures of the card and
// the pure geometry, so a test states each beat without a layer. Every
// figure is a figure of the card, in map px and wall seconds.

import type { CircleSink, SegmentSink } from './batches';
import { clamp01, ease } from '../../render/recipe-kit';

/** The figures of card 01. */
export const BOARDING_LOOK = {
  /** The ring round the boarded hull. */
  ringR: 17,
  ringW: 1.6,
  ringA: 0.14,
  /** The arc of the share done on the ring, from north, clockwise. */
  arcW: 1.8,
  arcA: 0.9,
  /** A grapple line. */
  lineW: 0.9,
  lineA: 0.85,
  /** The shot of a grapple: a dashed line that reaches the hull. */
  shootSec: 0.6,
  shootA: 0.8,
  dash: 2,
  /** The flash where a grapple strikes, until this many s after the start. */
  strikeSec: 0.9,
  strikeR: 2.5,
  /** The slack of a fresh line, which pulls taut by half the share. */
  sag: 5,
  /** The snap of a stopped boarding. */
  snapSec: 1.2,
  snapFlashSec: 0.3,
  snapFlashR: 3,
  /** The hand-over of a taken hull. */
  handSec: 1.2,
  /** The ring and the lines fade over this share of the hand-over. */
  handFade: 0.6,
  /** The wipe runs over this share of the hand-over, bow to stern. */
  wipeEnd: 0.8,
  wipeFrom: 14,
  wipeTo: -10,
  /** The bright edge of the wipe, while the hand-over is under this share. */
  edgeEnd: 0.85,
  edgeHalf: 9,
  edgeA: 0.7,
  edgeW: 1,
} as const;

/**
 * The share of the boarding done now, 0 to 1: the last share the wire
 * sent, grown toward 1 over the map time the wire said was left.
 * `elapsedMapMs` is the map time since that frame.
 */
export function boardingShare(milli: number, leftMs: number, elapsedMapMs: number): number {
  const p0 = clamp01(milli / 1000);
  if (leftMs <= 0) return p0;
  return p0 + (1 - p0) * clamp01(elapsedMapMs / leftMs);
}

/** The share of the ring arc while a boarding is stopped `stop` (0 to 1)
 *  into its snap: the ring drains from the share it had. */
export function drainedShare(p: number, stop: number): number {
  return stop > 0 ? p * (1 - ease(stop)) : p;
}

/** The local x of the wipe edge, `h` (0 to 1) into the hand-over. The part
 *  of the hull ahead of it wears the swallowtail. */
export function wipeX(h: number): number {
  const L = BOARDING_LOOK;
  return L.wipeFrom + (L.wipeTo - L.wipeFrom) * ease(h / L.wipeEnd);
}

/** A hull pose in map px. */
export interface Pose { x: number; y: number; h: number }

/** A point of a hull, in its own frame, put into the map. */
function toMap(p: Pose, lx: number, ly: number): [number, number] {
  const c = Math.cos(p.h), s = Math.sin(p.h);
  return [p.x + lx * c - ly * s, p.y + lx * s + ly * c];
}

/** The two ends of grapple `k` (0 or 1): the bow of the pirate, and a
 *  point on the boarded hull, one forward to port and one aft to
 *  starboard. */
export function grappleEnds(pirate: Pose, target: Pose, k: number): [number, number, number, number] {
  const [ax, ay] = toMap(pirate, 2, 0);
  const [bx, by] = toMap(target, 3 - k * 6, k ? 1.6 : -1.6);
  return [ax, ay, bx, by];
}

/** The segments of one slack line: a quadratic curve as a polyline. */
const CURVE_STEPS = 8;

/**
 * Grapple `k` while the boarding runs. `tau` is the wall s since the look
 * first saw the boarding, `p` the share done. The line shoots for the
 * first 0.6 s, a flash marks the strike, and the slack pulls taut by half
 * the share.
 */
export function packGrapple(
  segs: SegmentSink, rings: CircleSink, pirate: Pose, target: Pose,
  k: number, tau: number, p: number, fade: number, ink: number, flash: number,
): void {
  const L = BOARDING_LOOK;
  const [ax, ay, bx, by] = grappleEnds(pirate, target, k);
  const shoot = clamp01(tau / L.shootSec);
  if (shoot < 1) {
    segs.line(ax, ay, ax + (bx - ax) * shoot, ay + (by - ay) * shoot,
      L.lineW, ink, L.shootA * fade, 0, L.dash, L.dash);
    return;
  }
  const mx = (ax + bx) / 2, my = (ay + by) / 2;
  const nx = -(by - ay), ny = bx - ax, nl = Math.hypot(nx, ny) || 1;
  const sag = L.sag * (1 - ease(p * 2)) * (k ? 1 : -1);
  const cx = mx + (nx / nl) * sag, cy = my + (ny / nl) * sag;
  let px = ax, py = ay;
  for (let i = 1; i <= CURVE_STEPS; i++) {
    const t = i / CURVE_STEPS, u = 1 - t;
    const qx = u * u * ax + 2 * u * t * cx + t * t * bx;
    const qy = u * u * ay + 2 * u * t * cy + t * t * by;
    segs.line(px, py, qx, qy, L.lineW, ink, L.lineA * fade);
    px = qx; py = qy;
  }
  if (tau < L.strikeSec) rings.disc(bx, by, L.strikeR, flash, Math.min(1, 0.8 * (L.strikeSec - tau) / 0.3));
}

/**
 * Grapple `k` of a stopped boarding, `stop` (0 to 1) into its snap: each
 * half springs back from the middle to its own end and fades, and a flash
 * marks the break.
 */
export function packSnap(
  segs: SegmentSink, rings: CircleSink, pirate: Pose, target: Pose,
  k: number, stop: number, ink: number, flash: number,
): void {
  const L = BOARDING_LOOK;
  const [ax, ay, bx, by] = grappleEnds(pirate, target, k);
  const mx = (ax + bx) / 2, my = (ay + by) / 2;
  const e = ease(stop), a = L.lineA * (1 - e);
  segs.line(ax, ay, ax + (mx - ax) * (1 - e), ay + (my - ay) * (1 - e), L.lineW, ink, a);
  segs.line(bx, by, bx + (mx - bx) * (1 - e), by + (my - by) * (1 - e), L.lineW, ink, a);
  if (stop < L.snapFlashSec / L.snapSec) {
    rings.disc(mx, my, L.snapFlashR, flash, 0.8 * (1 - stop / (L.snapFlashSec / L.snapSec)));
  }
}

/** The ring round a boarded hull with the arc of the share `p`. */
export function packRing(rings: CircleSink, target: Pose, p: number, fade: number, ink: number): void {
  const L = BOARDING_LOOK;
  rings.ring(target.x, target.y, L.ringR, L.ringW, ink, L.ringA * fade);
  if (p > 0) rings.arc(target.x, target.y, L.ringR, L.arcW, -Math.PI / 2, p * Math.PI * 2, ink, L.arcA * fade);
}

/** The bright edge of the wipe at local x `X` across the taken hull. */
export function packWipeEdge(segs: SegmentSink, hull: Pose, X: number, flash: number): void {
  const L = BOARDING_LOOK;
  const [x0, y0] = toMap(hull, X, -L.edgeHalf);
  const [x1, y1] = toMap(hull, X, L.edgeHalf);
  segs.line(x0, y0, x1, y1, L.edgeW, flash, L.edgeA);
}

/** The atlas rectangle and the box a crop of a cell needs. */
export interface CropEntry { u0: number; v0: number; u1: number; v1: number; w: number; h: number; ox: number; oy: number }

/** What a crop draws: the quad's box centre against the hull origin, its
 *  width, and the two u bounds. Null when the crop holds no ink. */
export interface Crop { cx: number; w: number; u0: number; u1: number }

/**
 * The part of a cell between the local x `from` and `to` of its hull,
 * where the cell stands on the hull origin (the cell's box centre sits at
 * `ox, oy`). The wipe draws the class mark aft of the edge and the
 * swallowtail ahead of it, from the two cells as they are baked.
 */
export function cropOf(e: CropEntry, from: number, to: number): Crop | null {
  const left = e.ox - e.w / 2, right = e.ox + e.w / 2;
  const a = Math.max(left, from), b = Math.min(right, to);
  if (b <= a || e.w <= 0) return null;
  const du = e.u1 - e.u0;
  return { cx: (a + b) / 2, w: b - a, u0: e.u0 + ((a - left) / e.w) * du, u1: e.u0 + ((b - left) / e.w) * du };
}
